import { makeAutoObservable } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";
import { utils, uniqueArray, getDateRange } from "@intrasync/utils";
import ProductionOverviewRightHeaderCell from "./ProductionOverviewScheduleCells/ProductionOverviewSchedule.RightHeaderCell";
import ProductionOverviewRightBodyCell from "./ProductionOverviewScheduleCells/ProductionOverviewSchedule.RightBodyCell";
import ProductionOverviewLeftHeaderCell from "./ProductionOverviewScheduleCells/ProductionOverviewSchedule.LeftHeaderCell";
import ProductionOverviewLeftBodyCell from "./ProductionOverviewScheduleCells/ProductionOverviewSchedule.LeftBodyCell";

class ProductionOverviewScheduleStore {
  hiddenColumns: number[] = [];
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  setHiddenColumn = (columnIndex: number) => {
    if (this.hiddenColumns.includes(columnIndex)) {
      this.hiddenColumns = this.hiddenColumns.filter(
        (column) => column !== columnIndex
      );
      return;
    }
    this.hiddenColumns.push(columnIndex);
  };

  get jobNumbersWithinDateInRange() {
    const routerStore = this.rootStore.routerStore;
    if (!routerStore?.queryParams) return [];
    const { startDate, endDate } = routerStore.queryParams as {
      startDate: string;
      endDate: string;
    };

    const filteredJobNumbers = this.allEntities
      .filter(
        (entity) =>
          entity.castingDate &&
          entity.jobNumber &&
          entity.castingDate >= startDate &&
          entity.castingDate <= endDate
      )
      .map((entity) => entity.jobNumber as number);

    const currentEntitiesJobNumbers = uniqueArray(
      filteredJobNumbers
    ) as number[];
    return currentEntitiesJobNumbers.sort((a, b) => a - b);
  }

  // Get the actual date values for columns
  get dateValues(): string[] {
    const routerStore = this.rootStore.routerStore;
    if (!routerStore?.queryParams) return [];
    const { startDate, endDate } = routerStore.queryParams as {
      startDate: string;
      endDate: string;
    };

    return getDateRange(startDate, endDate, { weekdays: true });
  }

  // Calculate scheduled manhours for a specific date
  getScheduledManHoursForDate = (date: string): number => {
    return this.entities
      .filter((entity) => entity.castingDate === date)
      .reduce((sum, entity) => sum + ((entity.estimatedManHours as number) || 0), 0);
  };

  // Get capacity for a specific date from manhour capacity standards
  getCapacityForDate = (date: string): { capacity: number; workerCount: number | null; hoursPerWorker: number | null } => {
    const manhourCapacityStore = this.rootStore.manhourCapacityStore as ServiceStore;
    if (!manhourCapacityStore?.entities) {
      return { capacity: 0, workerCount: null, hoursPerWorker: null };
    }

    const standards = manhourCapacityStore.entities as unknown as Array<{
      effectiveDate: string;
      totalDailyCapacity: number;
      workerCount: number;
      hoursPerWorker: number;
    }>;

    const applicable = standards
      .filter((c) => c.effectiveDate <= date)
      .sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate))[0];

    if (applicable) {
      return {
        capacity: applicable.totalDailyCapacity,
        workerCount: applicable.workerCount,
        hoursPerWorker: applicable.hoursPerWorker,
      };
    }

    return { capacity: 0, workerCount: null, hoursPerWorker: null };
  };

  // TRANSPOSED: Columns are now DATES, Rows are now JOBS
  get columnHeaders() {
    // Date columns (dates on top)
    const dateColumns = this.dateValues.map((date) => {
      return {
        width: 180,
        title: date,
        key: date,
        headerKey: "castingDate",
        date: date,
        component: ProductionOverviewRightHeaderCell,
        bodyComponent: ProductionOverviewRightBodyCell,
      };
    });

    return [
      {
        width: 150,
        title: "Job",
        key: "jobNumber",
        component: ProductionOverviewLeftHeaderCell,
        bodyComponent: ProductionOverviewLeftBodyCell,
      },
      ...dateColumns,
    ];
  }

  get allEntities() {
    const panelStore = this.rootStore.panelStore as ServiceStore;
    return panelStore.entities;
  }

  get entities() {
    if (!this.allEntities) return [];
    const currentEntities = this.allEntities.filter((entity) => {
      if (entity.castingDate === "1970-01-01") return false;

      return this.jobNumbersWithinDateInRange.includes(
        entity.jobNumber as number
      );
    });
    return currentEntities;
  }

  // IMPORTANT: ScheduleGridStore expects this to be called "dateRange" for rows
  // In our transposed view, rows are JOBS (not dates)
  get dateRange(): number[] {
    return this.jobNumbersWithinDateInRange.filter((jobNumber) => {
      return !this.hiddenColumns.includes(jobNumber);
    });
  }
}

export default ProductionOverviewScheduleStore;
